import type { ActionFunctionArgs, LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { json } from "@remix-run/node";
import { useFetcher, useLoaderData, useSearchParams } from "@remix-run/react";
import { useEffect, useMemo, useRef, useState } from "react";
import ReCaptcha from "react-google-recaptcha";
import { RiMailLine } from "react-icons/ri";
import { ClientOnly } from "remix-utils/client-only";
import { AuthenticityTokenInput } from "remix-utils/csrf/react";
import { useFormContext, ValidatedForm } from "remix-validated-form";

import { Alert } from "~/components/Alert";
import { ErrorComponent } from "~/components/error-boundary";
import { QuestionCard } from "~/components/QuestionCard";
import { Typo } from "~/components/Typo";
import { Button } from "~/components/ui/button";
import { Checkbox, ValidatedCheckbox } from "~/components/ui/forms/checkbox";
import { ValidatedFileInput } from "~/components/ui/forms/file-input";
import { ValidatedInput } from "~/components/ui/forms/input";
import { ValidatedRadio } from "~/components/ui/forms/radio";
import { type SelectProps, ValidatedSelect } from "~/components/ui/forms/select";
import { ValidatedTextarea } from "~/components/ui/forms/textarea";
import { PageContainer } from "~/components/ui/layout/page-container";
import { Wrapper } from "~/components/ui/layout/wrapper";
import { PageHeading } from "~/components/ui/page-heading";
import { useManagedFetcher } from "~/hooks/use-managed-fetcher";
import { getAddresses } from "~/models/address.server";
import { getCountries } from "~/models/country.server";
import { getProductBySlug } from "~/models/product.server";
import { sendContactFormMessage } from "~/models/request-contact.server";
import { getSession } from "~/models/session.server";
import { useRootMatchData } from "~/root";
import { checkCsrf } from "~/utils/csrf.server";
import { handleError } from "~/utils/errors.server";
import { parseMultipartWithFile } from "~/utils/file-upload.server";
import { getSeoMeta } from "~/utils/seo";
import { contactFormValidator } from "~/validators/contact";

export const meta: MetaFunction = (metaArgs) => {
  return getSeoMeta(metaArgs, {
    title: "Nous contacter",
    description:
      "Nous sommes à votre disposition pour répondre à toutes vos interrogations, ou si vous avez le moindre besoin d'éclaircissement.",
  });
};

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const productSlug = url.searchParams.get("product-slug") || null;

  try {
    const session = await getSession({ request });
    const contactId = session.get("activeContactId") || null;

    const [countries, addresses, product] = await Promise.all([
      getCountries({ request }),
      contactId ? getAddresses({ request, contactId }) : null,
      productSlug ? getProductBySlug({ slug: productSlug, request }) : null,
    ]);

    return json({ countries, addresses, product });
  } catch (error) {
    return handleError(error, {
      countries: null,
      addresses: null,
      product: null,
    });
  }
};

export const action = async ({ request }: ActionFunctionArgs) => {
  try {
    const formData = await parseMultipartWithFile({ request });

    await checkCsrf({ formData, headers: request.headers });

    return await sendContactFormMessage({ formData, request });
  } catch (error) {
    return handleError(error, {
      countries: null,
      addresses: null,
      product: null,
    });
  }
};

const ContactUsPage = () => {
  const { countries, addresses, product } = useLoaderData<typeof loader>();
  const rootData = useRootMatchData();

  const [contactChecked, setContactChecked] = useState(false);
  const [personalDataChecked, setPersonalDataChecked] = useState(false);

  const [recaptchaToken, setRecaptchaToken] = useState("");

  const fetcher = useFetcher<any>();
  const [isFetcherLoading, isFetcherDone] = useManagedFetcher({
    fetcher,
    toastSuccessMessage:
      "Votre message nous à été transmis, nous vous répondrons dans les plus brefs délais",
  });
  const citiesFetcher = useFetcher<any>();
  const [isCitiesLoading] = useManagedFetcher({ fetcher: citiesFetcher });

  const countryInputRef = useRef<HTMLSelectElement>(null);
  const zipcodeInputRef = useRef<HTMLInputElement>(null);
  const cityInputRef = useRef<HTMLInputElement & HTMLSelectElement>(null);

  const countryCodeOptions: SelectProps["options"] =
    countries?.map((country) => ({
      label: country.label,
      value: country.id.toString(),
    })) || [];

  const billingAddress = addresses?.find((a) => a.categoryAddress === "billing");

  const defaultCountryId = billingAddress?.countryCode
    ? countries?.find((c) => c.alpha2Code === billingAddress.countryCode)?.id.toString()
    : countries?.find((c) => c.alpha2Code === "FR")?.id.toString();

  const [contactType, setContactType] = useState("pro");

  const { reset } = useFormContext("contact-form");

  // Reset form when fetcher is done
  useEffect(() => {
    if (isFetcherDone) {
      reset();
    }
  }, [isFetcherDone, reset]);

  const breadcrumbData = [{ name: "Nous contacter", slug: "" }];

  const [searchParams] = useSearchParams();
  const subjectParam = searchParams.get("subject") || "";

  const defaultSubject = useMemo(() => {
    switch (subjectParam) {
      case "estimate":
        return "Demande de devis";
      case "after-sales-service":
        return "Demande de SAV";
      case "informations":
        return "Demande de renseignements";
      case "other":
        return "Autre";
      default:
        return "Demande de devis";
    }
  }, [subjectParam]);

  const defaultMessage = useMemo(() => {
    if (product && (subjectParam === "estimate" || subjectParam === "informations")) {
      return `Bonjour,
Je suis intéressé(e) par le produit ${product.reference} (${product.title}) de la marque "${product.brand?.title}".
Pourriez-vous me recontacter afin de me communiquer plus d'informations ?
Merci.`;
    }
    return "";
  }, [product, subjectParam]);

  return (
    <PageContainer>
      <PageHeading
        breadcrumbData={breadcrumbData}
        headingText={"Nous contacter"}
        HeadingIconComponent={<RiMailLine />}
      />
      <Wrapper className="flex flex-col items-center gap-4 lg:flex-row lg:items-start lg:gap-8">
        <div className="flex-1 rounded-2xl bg-grey-100 p-12">
          <div className="mb-12">
            <Typo as="p" fontSize="base" fontColor="grey-700">
              Nous sommes à votre disposition pour répondre à toutes vos interrogations, ou si vous
              avez le moindre besoin d&apos;éclaircissement.
              <br />
              <br />
              N&apos;hésitez pas, contactez-nous !
            </Typo>
          </div>
          <ValidatedForm
            id="contact-form"
            validator={contactFormValidator}
            fetcher={fetcher}
            method="POST"
            encType="multipart/form-data"
            noValidate
            defaultValues={{
              firstName: rootData?.contact?.firstName ?? "",
              lastName: rootData?.contact?.lastName ?? "",
              email: rootData?.contact?.email ?? "",
              phone: rootData?.contact?.phone1 ?? rootData?.contact?.cellphone ?? "",
              city: billingAddress?.city ?? "",
              zipcode: billingAddress?.zipCode ?? "",
              country: defaultCountryId || "",
              subject: defaultSubject || "",
              message: defaultMessage || "",
            }}
          >
            <AuthenticityTokenInput />
            <ValidatedInput
              type="text"
              id="favorite_brand"
              name="favorite_brand"
              className="sr-only !w-px"
              defaultValue=""
              aria-hidden="true"
              label="Marque favorite"
              required
            />
            {!rootData?.isUserConnected ? (
              <div className="mb-12">
                <h2 className="mb-8 text-3xl font-semibold">Vous êtes ?</h2>
                <div className="flex items-center gap-x-10">
                  <ValidatedRadio
                    label="Un professionnel"
                    name="contactType"
                    id="contactType-pro"
                    value="pro"
                    onChange={(e) => {
                      setContactType((e.target as HTMLInputElement).value);
                    }}
                    defaultChecked
                  />
                  <ValidatedRadio
                    label="Un particulier"
                    name="contactType"
                    id="contactType-customer"
                    value="customer"
                    onChange={(e) => {
                      setContactType((e.target as HTMLInputElement).value);
                    }}
                  />
                </div>
                {contactType === "customer" ? (
                  <Alert
                    message="Notre site ne propose pas de vente en direct aux particuliers. Votre demande sera retransmise à un professionnel qui sera en mesure de vous renseigner."
                    type="warning"
                    className="mt-8"
                  />
                ) : null}
              </div>
            ) : (
              <input type="hidden" id="contactType-forced" name="contactType" value={contactType} />
            )}
            <div className="mb-12">
              <h2 className="mb-8 text-3xl font-semibold">Vos informations personnelles</h2>
              <Typo fontSize="sm" fontColor="grey-600">
                Merci de compléter le formulaire ci-dessous. Tous les champs sont obligatoires pour
                un bon traitement de votre demande.
              </Typo>
              {contactType === "pro" ? (
                <ValidatedSelect
                  id="subject"
                  name="subject"
                  label="Objet de votre demande"
                  options={[
                    {
                      label: "Demande de devis",
                      value: "Demande de devis",
                    },
                    {
                      label: "Demande de SAV",
                      value: "Demande de SAV",
                    },
                    {
                      label: "Demande de renseignements",
                      value: "Demande de renseignements",
                    },
                    {
                      label: "Autre",
                      value: "Autre",
                    },
                  ]}
                  defaultValue={defaultSubject || ""}
                  className="mt-8 w-full"
                />
              ) : null}
              <div className="mb-8 mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2">
                <ValidatedInput
                  id="firstname"
                  name="firstName"
                  label="Prénom"
                  autoComplete="given-name"
                />
                <ValidatedInput
                  id="lastname"
                  name="lastName"
                  label="Nom"
                  autoComplete="family-name"
                />
              </div>
              <div className="mb-8 grid grid-cols-1 gap-8 sm:grid-cols-2">
                <ValidatedInput
                  id="contact-phone"
                  name="phone"
                  label="N° de téléphone"
                  autoComplete="tel"
                />
                <ValidatedInput
                  name="email"
                  label="Email"
                  id="contact-email"
                  autoComplete="email"
                />
              </div>
              <div className="mb-8 grid grid-cols-1 gap-8 sm:grid-cols-2">
                <ValidatedSelect
                  ref={countryInputRef}
                  id="country"
                  name="country"
                  label="Pays"
                  options={countryCodeOptions}
                  className="w-full"
                  defaultValue={defaultCountryId || ""}
                />
                <ValidatedInput
                  id="zipcode"
                  name="zipcode"
                  label="Code postal"
                  onChange={(e) => {
                    const alpha2Code = countries?.find(
                      (c) => c.id.toString() === countryInputRef.current?.value
                    )?.alpha2Code;
                    if (cityInputRef.current?.value) {
                      cityInputRef.current.value = "";
                    }
                    if (e.currentTarget.value && alpha2Code) {
                      citiesFetcher.load(`/cities/${e.currentTarget.value}/${alpha2Code}`);
                    }
                  }}
                />
              </div>
              <div className="mb-8 grid grid-cols-1 gap-8 sm:grid-cols-2">
                {!citiesFetcher.data || citiesFetcher.data?.length === 0 ? (
                  <ValidatedInput
                    type="text"
                    id="city"
                    name="city"
                    label="Ville"
                    ref={cityInputRef}
                    disabled={!!isCitiesLoading || zipcodeInputRef.current?.value === ""}
                    infoMessage="Entrez le code postal pour charger les villes disponibles."
                  />
                ) : (
                  <ValidatedSelect
                    id="city"
                    name="city"
                    label="Ville"
                    options={citiesFetcher.data.map((c: string) => ({ label: c, value: c }))}
                    defaultValue={citiesFetcher.data?.length === 1 ? citiesFetcher.data[0] : ""}
                    placeholder="Selectionnez la ville"
                    ref={cityInputRef}
                    disabled={!!isCitiesLoading || zipcodeInputRef.current?.value === ""}
                  />
                )}
              </div>
            </div>
            <div className="mb-12">
              <h2 className="mb-8 text-3xl font-semibold">Votre message</h2>
              <ValidatedTextarea
                id="message"
                name="message"
                label="Message"
                defaultValue={defaultMessage || ""}
                className="mb-8 !h-40"
              />
              <ValidatedFileInput
                id="files"
                name="files"
                label="Pièces jointes"
                maxSize="10 Mo"
                className="mb-4"
                multiple
              />
            </div>
            <div className="mb-12">
              <ValidatedCheckbox
                id="checkboxContact"
                name="checkboxContact"
                checked={contactChecked}
                onChange={() => setContactChecked((prev) => !prev)}
                label="J'accepte d'être recontacté dans le cadre de ma demande d'informations ou d'une affaire. (obligatoire)"
              />
              {contactType === "customer" ? (
                <Checkbox
                  id="dataProcessingAllowed"
                  name="dataProcessingAllowed"
                  checked={personalDataChecked}
                  onChange={() => setPersonalDataChecked((prev) => !prev)}
                  label="J'accepte que mes coordonnées et informations personnelles soient retransmises à un professionnel afin d'être recontacté par ce dernier dans le cadre de ma demande d'informations."
                  className="mt-4"
                />
              ) : null}
            </div>
            <ClientOnly fallback={null}>
              {() => (
                <ReCaptcha
                  sitekey={window?.ENV?.RECAPTCHA_SITE_KEY ?? ""}
                  onChange={(token) => setRecaptchaToken(token || "")}
                  className="mb-8"
                />
              )}
            </ClientOnly>
            <ValidatedInput type="hidden" id="recaptcha" name="recaptcha" value={recaptchaToken} />
            <Button
              type="submit"
              loadingState={isFetcherLoading ? "loading" : "idle"}
              className="ml-auto"
            >
              Envoyer
            </Button>
          </ValidatedForm>
        </div>
        <div className="basis-80">
          <QuestionCard />
        </div>
      </Wrapper>
    </PageContainer>
  );
};

export default ContactUsPage;

export const ErrorBoundary = () => {
  return <ErrorComponent />;
};
